/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, {Component} from 'react';
import {connect} from 'react-redux';
import {injectIntl} from 'react-intl';
import queryString from 'query-string';

import {Article, DiscoverAnalytics, DiscoverUtils} from 'discover-hub';
import LinksService from '../../services/LinksService';
import {getEnvironment} from '../../../utils/Environment';
import {error, success} from '@react/react-spectrum/Toast';
import {setToastPlacement} from '@react/react-spectrum/Toast/js/state';
import DiscoverAnalyticsReact from '../../../utils/DiscoverAnalytics';
import open from '../../../utils/open';
import getConfig from '../../../config';
import {addMiniPlanModal} from '../../components/MiniPlanModal/MiniPlanModal';
import TargetUtils from '../../../utils/TargetUtils';
import './Discover.scss';
import {DeviceDetectionUtil} from 'cchome-shared/utils';
import CCStorageService from '../../services/CCStorageService';
import SupportService from '../../services/SupportService';

type Props = {
	locale: string;
	env: string;
	route: Object;
	history: Array<any>;
	location: any;
	intl: any;
};

type State = {
	message: Object;
	ingestData: any;
};

declare let adobeIMS: AdobeIMS;

const analytics = new DiscoverAnalyticsReact();

class ArticlePage extends Component<Props, State> {
	constructor(props) {
		super(props);
		this.state = {
			message: {},
			ingestData: {}, // populated in discover hub when server has finished getting article data
		};
		setToastPlacement('bottom');
		this.onReceiveMessage = this.onReceiveMessage.bind(this);
	}

	/**
	 * Function for interpretting messages from iFrame (from Wrecking ball team)
	 * @param {object} event
	 */
	onReceiveMessage(event) {
		if (event && event.data) {
			try {
				const data = JSON.parse(event.data);
				if (data && data.message) {
					if (data.message === 'create::SaveToLib') {
						this.sendMessage('SavingToLib', data.pubLibId);
						this.saveToLibrary(data.pubLibId)
							.then((result: any) => {
								const eventData = DiscoverAnalytics.postAddLibraryStatus(
									this.state.ingestData.topic,
									this.state.ingestData.article,
									true,
								);
								analytics.postCustomData(eventData);

								this.sendMessage('SavedToLib', data.pubLibId);
								let toastMessage = this.props.intl.formatMessage({id: 'addToLibrary.added'});

								if (window.cchome.util.hasFeature('follow_library')) {
									toastMessage = this.props.intl.formatMessage({id: 'addToLibrary.added'});
									success(toastMessage, {
										actionLabel: this.props.intl.formatMessage({id: 'addToLibrary.manage'}),
										onAction: () =>
											open(`${getConfig('ASSET_LIBRARY')}?filter=following`, '_blank'),
										timeout: 0,
									});
								}
								else {
									if (result && result.name) {
										toastMessage = this.props.intl.formatMessage(
											{id: 'addToLibrary.addedFile'},
											{filename: result.name},
										);
									}
									success(toastMessage, {
										actionLabel: this.props.intl.formatMessage({id: 'addToLibrary.manage'}),
										onAction: () => open(`${getConfig('ASSET_LIBRARY')}?filter=owned`, '_blank'),
										timeout: 0,
									});
								}
							})
							.catch((errorData) => {
								const eventData = DiscoverAnalytics.postAddLibraryStatus(
									this.state.ingestData.topic,
									this.state.ingestData.article,
									false,
								);
								analytics.postCustomData(eventData);

								this.sendMessage('FailedSaveToLib', data.pubLibId);
								error(this.props.intl.formatMessage({id: 'addToLibrary.error'}), {
									timeout: 0,
								});
							});
					}

					else if (data.message === 'create::CopyAsset') {
						// eslint-disable-next-line max-depth
						if (window.cchome.util.hasFeature('ctw-asset-ref')) {
							// send copying message
							this.sendMessage('CopyingAssetRef', data.pubLibId);
							const supportService = new SupportService();
							return supportService
								.fetchAssetRef(data.pubLibId)
								.then(({sourceAssetUrn, destinationPath}) => this.copyAsset(sourceAssetUrn, destinationPath).then(() => {
									const contentPath = destinationPath?.split('/') || [];
									const fileAction
											= contentPath[1] === 'cloud-content' ? 'ASSET_CLOUD_DOCS' : 'ASSET_CC_FILES';

									// send copy complete message
									this.sendMessage('CopiedAssetRef', data.pubLibId);
									const toastMessage = this.props.intl.formatMessage(
										{id: 'copyToYourWork.copied'},
										{
											yourWork: (
												<b>{`"${this.props.intl.formatMessage({id: 'app.navtab.file'})}"`}</b>
											),
										},
									);

									success(toastMessage, {
										actionLabel: this.props.intl.formatMessage({
											id: 'copyToYourWork.view',
										}),
										onAction: () => open(getConfig(fileAction), '_blank'),
										timeout: 0,
										closeOnAction: true,
									});
								}))
								.catch(() => {
									const toastMessage = this.props.intl.formatMessage(
										{id: 'copyToYourWork.error'},
										{
											yourWork: (
												<b>{`"${this.props.intl.formatMessage({id: 'app.navtab.file'})}"`}</b>
											),
										},
									);

									// send copy failed message
									this.sendMessage('FailedCopyAssetRef', data.pubLibId);

									error(toastMessage, {
										timeout: 0,
										closeOnAction: true,
									});
								});
						}
						// TODO: Remove this block once the intergration testing is complete
						this.sendMessage('CopyingAsset', data.assetUrn);
						return this.checkAssetName(data.targetPath).then(path => this.copyAsset(data.assetUrn, path)
							.then((result: any) => {
								// const eventData = DiscoverAnalytics.postAddLibraryStatus(
								// 	this.state.ingestData.topic,
								// 	this.state.ingestData.article,
								// 	true,
								// );
								// analytics.postCustomData(eventData);

								const contentPath = path?.split('/') || [];
								const fileAction
											= contentPath[1] === 'cloud-content' ? 'ASSET_CLOUD_DOCS' : 'ASSET_CC_FILES';

								this.sendMessage('CopiedAsset', data.assetUrn);
								const toastMessage = this.props.intl.formatMessage(
									{id: 'copyToYourWork.copied'},
									{
										yourWork: (
											<b>{`"${this.props.intl.formatMessage({id: 'app.navtab.file'})}"`}</b>
										),
									},
								);

								success(toastMessage, {
									actionLabel: this.props.intl.formatMessage({id: 'copyToYourWork.view'}),
									onAction: () => open(getConfig(fileAction), '_blank'),
									timeout: 0,
									closeOnAction: true,
								});
							})
							.catch(() => {
								// const eventData = DiscoverAnalytics.postAddLibraryStatus(
								// 	this.state.ingestData.topic,
								// 	this.state.ingestData.article,
								// 	false,
								// );
								// analytics.postCustomData(eventData);

								this.sendMessage('FailedCopyAsset', data.pubLibId);

								const toastMessage = this.props.intl.formatMessage(
									{id: 'copyToYourWork.error'},
									{
										yourWork: (
											<b>{`"${this.props.intl.formatMessage({id: 'app.navtab.file'})}"`}</b>
										),
									},
								);

								error(toastMessage, {
									timeout: 0,
									closeOnAction: true,
								});
							}));

					}
					else if (data.message === 'create::AnalyticsTracking') {
						const eventData = DiscoverAnalytics.postIFrameAnalytics(
							data.data,
							this.state.ingestData.topic,
							this.state.ingestData.article,
							window.cchome.util.getAllFeatures(),
						);
						analytics.postCustomData(eventData);
					}
				}
			}
			catch (error) {
				console.error('Failed to interpret WB message', error);
				return;
			}
		}
	}

	/**
	 * Send message to iFrame about the status of saving to library.
	 * @param {string} status
	 * @param {*} id
	 */
	sendMessage(status, id) {
		let message = '';
		switch (status) {
			case 'SavingToLib':
				message = 'cchome::SavingToLib';
				this.setState({
					message: {
						message,
						pubLibId: id,
					},
				});
				break;

			case 'SavedToLib':
				message = 'cchome::SavedToLib';
				this.setState({
					message: {
						message,
						pubLibId: id,
					},
				});
				break;

			case 'FailedSaveToLib':
				message = 'cchome::FailedSaveToLib';
				this.setState({
					message: {
						message,
						pubLibId: id,
					},
				});
				break;

			case 'CopyingAsset':
				message = 'cchome::CopyingAsset';
				this.setState({
					message: {
						message,
						assetUrn: id,
					},
				});
				break;

			case 'CopiedAsset':
				message = 'cchome::CopiedAsset';
				this.setState({
					message: {
						message,
						assetUrn: id,
					},
				});
				break;

			case 'FailedCopyAsset':
				message = 'cchome::FailedCopyAsset';
				this.setState({
					message: {
						message,
						assetUrn: id,
					},
				});
				break;

			case 'CopyingAssetRef':
				message = 'cchome::CopyingAsset';
				this.setState({
					message: {
						message,
						pubLibId: id,
					},
				});
				break;

			case 'CopiedAssetRef':
				message = 'cchome::CopiedAsset';
				this.setState({
					message: {
						message,
						pubLibId: id,
					},
				});
				break;

			case 'FailedCopyAssetRef':
				message = 'cchome::FailedCopyAsset';
				this.setState({
					message: {
						message,
						pubLibId: id,
					},
				});
				break;
		}
	}

	checkAssetName(path) {
		const arr = path.split('/');
		const file = arr[arr.length - 1];
		const fileArr = file.split('.');
		const extension = fileArr[fileArr.length - 1];
		const fileName = fileArr.slice(0, fileArr.length - 1).join('.');
		// fileName could contain multiple dots
		// fileName example: a.b.c.psdc -> [a, b, c, psdc] -> [a,b,c] -> a.b.c

		return new Promise<string>((res, rej) => new CCStorageService()
			.getValidAssetName(fileName, extension)
			.then((newFile) => {
				arr[arr.length - 1] = newFile;
				res(arr.join('/'));
			})
			.catch((e) => {
				rej(e);
			}));
	}

	copyAsset(urn, path) {
		return new CCStorageService().copyAsset(urn, path);
	}

	saveToLibrary(pubLibId) {
		if (window.cchome.util.hasFeature('follow_library')) {
			if (getEnvironment() !== 'prod') {
				return new Promise((resolve, reject) => {
					new LinksService()
						.followLibrary('73760fbb-cedb-47f7-52f3-4bdb6d8d7e10')
						.then((result) => {
							resolve(result);
						})
						.catch(error => reject(error));
				});
			}
			return new Promise((resolve, reject) => {
				new LinksService()
					.followLibrary(pubLibId)
					.then((result) => {
						resolve(result);
					})
					.catch(error => reject(error));
			});
		}

		return new Promise((resolve, reject) => {
			new LinksService()
				.saveToLibrary(pubLibId)
				.then((result) => {
					resolve(result);
				})
				.catch(error => reject(error));
		});
	}

	/**
	 * Returns IMS Data required for making authorized server requests for discover-hub
	 * @returns {object}
	 */
	getIMSData() {
		return {
			bearerToken: adobeIMS.getAccessToken(),
			// @ts-ignore
			userId: window.cchome.util.getUserId(),
		};
	} // TODO: MAKE THIS A COMMON FUNCTION between discover.js and article.js

	/**
	 * Callback function to be passed into discover-hub
	 * component to allow route changes.
	 *
	 * @param {any} arg1
	 * @param {bool} isArticle
	 */
	onDiscoverPathChange(arg1) {
		// This is an array, contain the signals
		// [{type: "topic", value: "photography"},
		// {type: "pageIndex", value: 1},
		// {type: "discoverCategory", value: "inspiration"}]
		const signals = arg1;
		let newPath = this.props.location.pathname; // ex. /cc/en/discover/inspiration
		const newState: any = {};

		let isArticle = false;
		let isHomeRoute = false;

		const newSearchParams = signals.reduce((params, signal) => {
			// For Article pages, the only signal we should really be getting is Article signals anyway
			// TODO: MAKE COMMON THIS LOGIC BETWEEN DISCOVER.JS AND ARTICLE.JS
			if (signal.type === 'topic') {
				// ..?topic=photography
				newState.topic = signal.value;

				if (signal.value !== 'all-topics') {
					params.filter = signal.value;
				}
			}
			else if (signal.type === 'pageIndex') {
				// ..?page=1
				newState.pageIndex = signal.value;

				if (signal.value !== 0) {
					params.page = signal.value + 1;
				}
			}
			else if (signal.type === 'discoverCategory') {
				// cc/en/discover/inspiration
				const beforeDiscover = newPath.split('/discover')[0];
				const extraPath = signal.value === 'featured' ? '' : `${signal.value}/`;
				newPath = `${beforeDiscover}/discover/${extraPath}`;
				newState.category = signal.value;
			}
			else if (signal.type === 'article') {
				// push this path with go to a different route
				const beforeDiscover = newPath.split('/discover')[0];
				const extraPath = `${signal.value.articleSlug}`;
				newPath = `${beforeDiscover}/discover/article/${extraPath}`; // NOTE: This drops all query params

				isArticle = true;
			}
			else if (signal.type === 'pdp') {
				newPath = signal.value;
				isHomeRoute = true;
			}
			return params;
		}, {});

		if (isArticle) {
			this.props.history.push({
				pathname: `${newPath}`,
			});
			// this.setState(newState);
		}
		else if (isHomeRoute) {
			this.props.history.push(newPath);
		}
		else {
			this.props.history.push(`${newPath}${DiscoverUtils.getSearchPath(newSearchParams)}`);
			// this.setState(newState);
		}
	}

	render() {
		let entityId = TargetUtils.getEntityId();

		let component;

		const queryIframeUrl = queryString.parse(this.props.location.search).iframeUrl;

		if (DeviceDetectionUtil.isInternetExplorer()) {
			TargetUtils.addEntityId(this.props.locale, entityId);

			component = (
				<div className='ie-error-page'>
					<p>{this.props.intl.formatMessage({id: 'app.ie11.error.message'})}</p>
				</div>
			);
		}
		else if (window.cchome.util.hasFeature('preview_article') && queryIframeUrl) {
			// to use this, use query param ?iframeUrl={full iframe url here}
			TargetUtils.addEntityId(this.props.locale, entityId);

			component = (
				<Article
					iframeUrl={queryIframeUrl}
					article={{}}
					onIngestData={ingestData =>
						Object.keys(this.state.ingestData).length === 0 && this.setState({ingestData})
					}
					onReceiveMessage={this.onReceiveMessage}
					message={this.state.message}
					locale={this.props.locale}
					imsData={this.getIMSData()}
					env={getEnvironment()}
					getTargetInfo={() => TargetUtils.getTargetInfo()}
					postAnalytics={data => analytics.postCustomData(data)}
					currentTab={this.props.location.state ? this.props.location.state.currentTab : null}
					onDiscoverPathChange={data => this.onDiscoverPathChange(data)}
					intl={this.props.intl}
					logger={window.cchome.util.logger}
					signedIn
					addMiniPlanModal={addMiniPlanModal}
					featureFlags={window.cchome.util.getAllFeatures()}
				/>
			);
		}
		else {
			const path = this.props.location.pathname.split('/'); // todo: figure out a better way to do this
			const indexOfArticle = path.indexOf('article');
			const articlePathInfo = {
				articleSlug: path[indexOfArticle + 1],
			};
			entityId = TargetUtils.getEntityId(articlePathInfo.articleSlug);
			TargetUtils.addEntityId(this.props.locale, entityId);

			const imsProfile = adobeIMS.getUserProfile();
			let countryCode = 'us';
			if (imsProfile && imsProfile.countryCode) {
				countryCode = imsProfile.countryCode.toLowerCase();
			}

			component = (
				<div className='discover-article-container'>
					<Article
						article={articlePathInfo}
						onIngestData={ingestData =>
							Object.keys(this.state.ingestData).length === 0 && this.setState({ingestData})
						}
						onReceiveMessage={this.onReceiveMessage}
						message={this.state.message}
						locale={this.props.locale}
						imsData={this.getIMSData()}
						env={getEnvironment()}
						getTargetInfo={() => TargetUtils.getTargetInfo(entityId, countryCode)}
						postAnalytics={data => analytics.postCustomData(data)}
						currentTab={this.props.location.state ? this.props.location.state.currentTab : null}
						onDiscoverPathChange={data => this.onDiscoverPathChange(data)}
						intl={this.props.intl}
						logger={window.cchome.util.logger}
						signedIn
						addMiniPlanModal={addMiniPlanModal}
						featureFlags={window.cchome.util.getAllFeatures()}
					/>
				</div>
			);
		}

		return component;
	}
}

function mapStateToProps() {
	return {};
}

export default connect(mapStateToProps, {})(injectIntl(ArticlePage));
